import { startVoiceSession, stopVoiceSession } from './speech';
import { templateTextWidget } from './template';
import { ChatUIElements, Nullable } from './types';

const widget = document.createElement('template');
widget.innerHTML = templateTextWidget;

class WitzoVoice extends HTMLElement {
  /**Url to connect to the webhook */
  _agentId: Nullable<string> = null;

  /**Reference for elements */
  chat: Nullable<ChatUIElements> = null;

  /**Is Initialized */
  isInitialized: boolean = false;

  /**Checks if mic is on or not */
  isMicActive: boolean = false;

  constructor() {
    super();
    const node = widget.content.cloneNode(true);
    //Assume no outside influence on the styling should be possible
    const root = this.attachShadow({ mode: 'closed' });
    root.appendChild(node);

    this.getElements(root);
  }

  /**
   * @description retrieve elements from shadow dom and ensures initialization
   */
  private getElements(root: ShadowRoot) {
    const q = <T extends HTMLElement | SVGSVGElement>(selector: string) =>
      root.querySelector(selector) as T;

    try {
      this.chat = {
        openButton: q('#voice-floating-btn'),
        chatWindow: q('#chatWidget'),
        minimizeButton: q('#minimize-btn'),
        micButton: q('#voice-btn'),
        icons: {
          start: q('.start-icon'),
          stop: q('.stop-icon'),
        },
        waves: {
          root: q('.voice-waves'),
          wavelets: Array.from(root.querySelectorAll('.wave')) as HTMLDivElement[],
        },
        status: q('#status-message'),
      };
      this.isInitialized = true;
    } catch (err) {
      this.isInitialized = false;
      console.warn(`Couldn't initialize ${err}`);
    }
  }

  /**
   * @description external function to set the url to connect to web-hook
   */
  set assignAgentId(id: string) {
    this._agentId = id;
  }

  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    if (!this.chat || this.isInitialized === false) return;
    const { openButton, minimizeButton, micButton, chatWindow, icons, status, waves } = this.chat;

    openButton.addEventListener('click', () => this.maximizeWidget());
    minimizeButton.addEventListener('click', () => this.minimizeWidget());
    micButton.addEventListener('click', async () => await this.toggleMic());
  }

  /**Minimises the chat window */
  minimizeWidget() {
    if (!this.chat) return;

    this.chat.chatWindow.classList.add('minimizing');
    setTimeout(() => {
      this.chat?.chatWindow.classList.add('hidden');
      this.chat?.chatWindow.classList.remove('minimizing');
      this.chat?.openButton.classList.remove('hidden');
    }, 300);
  }

  /**Maximize the chat window */
  maximizeWidget() {
    if (!this.chat) return;

    this.chat.openButton.classList.add('hidden');
    this.chat.chatWindow.classList.remove('hidden');
  }

  private setMicUIState(active: boolean) {
    if (!this.chat) return;

    const { status, icons } = this.chat;

    icons.start.classList.toggle('hidden', active);
    icons.stop.classList.toggle('hidden', !active);
    status.textContent = active ? 'Establishing Connection...' : 'Terminating Connection...';
  }

  async toggleMic() {
    if (!this._agentId || !this.chat) return;

    const { micButton, status, waves } = this.chat;

    micButton.classList.toggle('active');
    micButton.disabled = true;

    try {
      if (!this.isMicActive) {
        this.setMicUIState(true);
        startVoiceSession(this._agentId, status, waves.root, waves.wavelets);
      } else {
        this.setMicUIState(false);
        stopVoiceSession(status, waves.root);
      }

      this.isMicActive = !this.isMicActive;
    } catch (err) {
      console.error(`Error while mic toggle ${err}`);
      status.textContent = 'Error Occured. Please Try Again';
      this.isMicActive = !this.isMicActive;
    } finally {
      micButton.disabled = false;
    }
  }
}

export { WitzoVoice };
